import React from 'react';
import styled from 'styled-components';
import SectionHeading from '../common/SectionHeading';
import FeatureCard from '../common/FeatureCard';

// Import SVG icons
const MagnifyingGlassIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const ClipboardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

const ScalesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
  </svg>
);

const DollarShieldIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 8h6m-5 0a3 3 0 110 6H9l3 3m-3-6h6m6 1a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const SectionContainer = styled.section`
  background-color: #161616;
  color: #FFFFFF;
  padding: 100px 0;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
  margin-top: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SEOContentSection = styled.div`
  margin-top: 80px;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
`;

const SEOHeading = styled.h2`
  font-size: 32px;
  color: #2196F3;
  margin-bottom: 24px;
  font-weight: 700;
`;

const SEOSubheading = styled.h3`
  font-size: 24px;
  color: #FFD700;
  margin: 32px 0 16px;
  font-weight: 600;
`;

const SEODescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 20px;
  
  strong {
    color: #00C853;
    font-weight: 600;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 24px 0;
  text-align: left;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const FeatureListItem = styled.li`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 12px;
  padding-left: 24px;
  position: relative;
  
  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #00C853;
    font-weight: bold;
    font-size: 18px;
  }
`;

const FeaturesSection = () => {
  // Updated features based on app.carconsul.com
  const features = [
    {
      icon: <MagnifyingGlassIcon />,
      title: "AI-Powered Research Assistant",
      description: "Our advanced AI analyzes thousands of data points to provide personalized vehicle recommendations tailored to your specific needs",
      iconBackground: "#2196F3" // Electric blue
    },
    {
      icon: <ClipboardIcon />,
      title: "Comprehensive Vehicle Analysis",
      description: "Get detailed reports on reliability, common issues, maintenance costs, and real-world performance for any vehicle you're considering",
      iconBackground: "#00C853" // Emerald green
    },
    {
      icon: <ScalesIcon />,
      title: "Negotiation Scripts & Strategies",
      description: "Access proven negotiation templates and dealer-specific strategies that have helped users save an average of $400-$500 on their purchases",
      iconBackground: "#FFD700" // Rich gold
    },
    {
      icon: <DollarShieldIcon />,
      title: "Real-Time Market Value Analysis",
      description: "See exactly what others are paying for the same vehicle in your area with our proprietary pricing database",
      iconBackground: "#2196F3" // Electric blue
    }
  ];

  return (
    <SectionContainer id="features">
      <Content>
        <SectionHeading 
          title="Complete Car Buying Advisor & Price Negotiation Platform" 
          subtitle="Advanced AI technology combined with expert car buying strategies to help you negotiate better deals and avoid costly mistakes"
        />
        
        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              iconBackground={feature.iconBackground}
              animationDelay={index * 0.1}
            />
          ))}
        </FeaturesGrid>
        
        <SEOContentSection>
          <SEOHeading>Professional Car Buying Advisor at Your Fingertips</SEOHeading>
          <SEODescription>
            Our <strong>AI car buying advisor</strong> provides the same level of expertise as hiring a professional car buying consultant, but at a fraction of the cost. Whether you're a first-time buyer or experienced purchaser, our platform gives you the tools and knowledge to negotiate like a pro.
          </SEODescription>
          
          <SEOSubheading>Car Price Negotiation Made Simple</SEOSubheading>
          <SEODescription>
            Stop paying sticker price! Our <strong>car price negotiation app</strong> provides dealer-specific strategies, market-based pricing data, and proven scripts that have helped thousands of users secure better deals.
          </SEODescription>
          
          <FeatureList>
            <FeatureListItem>Personalized negotiation scripts based on vehicle and dealer</FeatureListItem>
            <FeatureListItem>Real-time market pricing analysis and comparisons</FeatureListItem>
            <FeatureListItem>Dealer reputation scores and review analysis</FeatureListItem>
            <FeatureListItem>Financing option evaluation and recommendations</FeatureListItem>
            <FeatureListItem>Trade-in value optimization strategies</FeatureListItem>
            <FeatureListItem>Contract review assistance and red flag detection</FeatureListItem>
            <FeatureListItem>Post-purchase support and warranty guidance</FeatureListItem>
          </FeatureList>
          
          <SEOSubheading>Independent Car Buying Consultant AI</SEOSubheading>
          <SEODescription>
            Unlike other car buying services that receive commissions from dealers, our <strong>car buying consultant AI</strong> provides completely unbiased advice. We work exclusively for you, ensuring every recommendation is in your best interest.
          </SEODescription>
          
          <SEODescription>
            Join thousands of satisfied customers who have saved an average of <strong>$3,200 per vehicle purchase</strong> using our professional car buying advisor platform. Get the insider knowledge and negotiation power typically reserved for industry professionals.
          </SEODescription>
        </SEOContentSection>
      </Content>
    </SectionContainer>
  );
};

export default FeaturesSection;
